import type { SichererSpeicher } from './sicherer-speicher';
import {
  AktivierungsFehler,
  INTERVALL_AUFSCHLAG_S,
  INTERVALL_MAX_S,
  INTERVALL_START_S,
  SITZUNG_GUELTIG_S,
  type AbholenErgebnis,
  type AktivierungsSchnittstelle,
  type GeraeteInfo,
  type OeffentlicherGeraeteschluessel,
  type StartAntwort,
} from './vertrag';
import {
  ALT_KASSENSCHLUESSEL,
  ALT_KASSEN_ID,
  SITZUNG_EINTRAG,
  ladeZugang,
  speichereZugang,
  type GeraeteZugang,
} from './zugang';

/**
 * Zustandsautomat der Geräteseite (Geräteaktivierung V2, Ersteinrichtung der Kasse), gegen
 * `docs/geraeteaktivierung/vertrag.md` Version 1.
 *
 * Ablauf: Start → Benutzercode/QR anzeigen („wartet“) → ein Administrator ordnet zu
 * („zuordnung“: Bestätigungscode groß anzeigen, der Admin tippt ihn ein) → bestätigt („laeuft“:
 * Gerät erzeugt den Kassenschlüssel, merkt ihn, sendet nur den Hash) → abgeholt → Zugang sicher
 * speichern → „erfolgreich“. Endzustände der Sitzung und fehlende Verbindung beim Start führen zu
 * „fehlgeschlagen“.
 *
 * Regeln:
 * - Abfragen nie schneller als `intervallSekunden`; `slow_down` erhöht es dauerhaft um 5 s
 *   (RFC 8628 3.5) bzw. auf den Wert des Servers.
 * - Netzwerkabbruch während der Sitzung beendet sie nicht: weiter versuchen (Rückzug bis 30 s),
 *   Anzeige „Verbindung unterbrochen“; erst der Ablauf beendet sie.
 * - Neustart während der Aktivierung: die Sitzung (Gerätegeheimnis und ggf. schon erzeugter
 *   Kassenschlüssel im sicheren Speicher) wird fortgesetzt, solange sie gültig ist; sonst sauber
 *   verworfen. Der Kassenschlüssel wird gespeichert, BEVOR sein Hash gesendet wird – so kann eine
 *   verlorene Antwort mit demselben Hash wiederholt werden (Vertrag: idempotent).
 * - Der Zugang gilt erst als übernommen, wenn er im sicheren Speicher lesbar steht; erst dann
 *   wird die Sitzung gelöscht.
 * - Antworten einer abgebrochenen/ersetzten Sitzung werden verworfen (Generationszähler).
 * - Kein Geheimnis (Gerätegeheimnis, Kassenschlüssel) erscheint in Anzeige, Fehlertext oder Log.
 * - Der Bestätigungscode kommt nur vom Server; das Gerät berechnet ihn nie.
 */

export type FehlerGrund =
  'abgelaufen' | 'abgebrochen' | 'gesperrt' | 'offline' | 'zuVieleVersuche' | 'speicher' | 'fehler';

export type Verbindung = 'ok' | 'unterbrochen';

export type AktivierungsAnzeige =
  | { art: 'nichtAktiviert' }
  | { art: 'startet' }
  | {
      art: 'wartet';
      benutzerCode: string;
      /** Leer = kein QR (unsichere/ungültige Adresse), nur den Code zeigen. */
      qrInhalt: string;
      gueltigBis: number;
      verbindung: Verbindung;
    }
  | {
      art: 'zuordnung';
      bestaetigungsCode: string;
      unternehmerName: string;
      kassenName: string;
      gueltigBis: number;
      verbindung: Verbindung;
    }
  | {
      art: 'laeuft';
      unternehmerName: string | null;
      kassenName: string | null;
      verbindung: Verbindung;
    }
  | { art: 'erfolgreich'; zugang: Omit<GeraeteZugang, 'nachweis'> }
  | { art: 'fehlgeschlagen'; grund: FehlerGrund; meldung: string };

export const FEHLERTEXTE: Readonly<Record<FehlerGrund, string>> = {
  abgelaufen: 'Der Aktivierungscode ist abgelaufen. Bitte einen neuen Code anfordern.',
  abgebrochen: 'Die Aktivierung wurde abgebrochen.',
  gesperrt:
    'Die Aktivierung wurde nach zu vielen falschen Bestätigungscodes gesperrt. Bitte einen neuen Code anfordern.',
  offline: 'Keine Verbindung zum Server. Bitte Internetverbindung prüfen und erneut versuchen.',
  zuVieleVersuche:
    'Zu viele Aktivierungsversuche von diesem Anschluss. Bitte später erneut versuchen.',
  speicher:
    'Der Zugang konnte auf diesem Gerät nicht sicher gespeichert werden. Bitte erneut aktivieren.',
  fehler: 'Die Aktivierung ist fehlgeschlagen. Bitte erneut versuchen.',
};

/** Im sicheren Speicher abgelegte, laufende Sitzung (für die Fortsetzung nach Neustart). */
interface GespeicherteSitzung {
  v: 2;
  geraeteGeheimnis: string;
  benutzerCode: string;
  qrInhalt: string;
  gueltigBis: number;
  intervall: number;
  unternehmerName?: string;
  kassenName?: string;
  /** Ab `bestaetigt`: Kassen-ID und selbst erzeugtes Geheimnis des Kassenschlüssels. */
  kassenId?: string;
  schluesselGeheim?: string;
}

function istSitzung(x: unknown): x is GespeicherteSitzung {
  if (typeof x !== 'object' || x === null) {
    return false;
  }
  const s = x as Record<string, unknown>;
  return (
    s.v === 2 &&
    typeof s.geraeteGeheimnis === 'string' &&
    typeof s.benutzerCode === 'string' &&
    typeof s.qrInhalt === 'string' &&
    typeof s.gueltigBis === 'number' &&
    typeof s.intervall === 'number' &&
    (s.kassenId === undefined || typeof s.kassenId === 'string') &&
    (s.schluesselGeheim === undefined || typeof s.schluesselGeheim === 'string')
  );
}

export const MAX_RUECKZUG_MS = 30_000;

/**
 * QR-Inhalt vom Server nur übernehmen, wenn er eine https-Adresse ist (http nur auf localhost in
 * der Entwicklung) – sonst wird kein QR angezeigt, nur der Code.
 */
export function pruefeQrInhalt(roh: string): string {
  let url: URL;
  try {
    url = new URL(roh);
  } catch {
    return '';
  }
  const lokal = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && lokal)) {
    return '';
  }
  return url.toString();
}

/** Benutzercode lesbar: „1234 5678“; Bestätigungscode: „123 456“. */
export function formatiereCode(code: string): string {
  if (/^\d{8}$/.test(code)) {
    return `${code.slice(0, 4)} ${code.slice(4)}`;
  }
  if (/^\d{6}$/.test(code)) {
    return `${code.slice(0, 3)} ${code.slice(3)}`;
  }
  return code;
}

/**
 * Lokale Frist aus `ablaufAm`. Weicht die Geräteuhr offensichtlich ab (Frist in der
 * Vergangenheit oder mehr als doppelt so lang wie vorgesehen), gilt die Vertragsdauer ab jetzt –
 * der Server entscheidet ohnehin über den Ablauf.
 */
export function lokaleFrist(ablaufAm: string, jetzt: number): number {
  const rest = Date.parse(ablaufAm) - jetzt;
  if (!Number.isFinite(rest) || rest <= 0 || rest > 2 * SITZUNG_GUELTIG_S * 1000) {
    return jetzt + SITZUNG_GUELTIG_S * 1000;
  }
  return jetzt + rest;
}

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) {
    s += String.fromCharCode(b);
  }
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function sha256Hex(text: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface AktivierungsOptionen {
  schnittstelle: AktivierungsSchnittstelle;
  speicher: SichererSpeicher;
  geraet: () => GeraeteInfo;
  /** Öffentlicher Geräteschlüssel (Stufe 2, vorbereitet), optional. */
  oeffentlicherSchluessel?: () => Promise<OeffentlicherGeraeteschluessel | null>;
  /**
   * Übergang: Kassen-ID und Kassenschlüssel zusätzlich wie bisher in localStorage spiegeln
   * (`bilanx.kassenId`, `bilanx.kassenschluessel`), solange App, Küchenmonitor und Kundenanzeige
   * den Schlüssel von dort lesen (Änderungswunsch AV3-4). Ohne Spiegel nur sicherer Speicher.
   */
  spiegel?: { setItem(schluessel: string, wert: string): void };
  zufall?: (n: number) => Uint8Array;
  hash?: (text: string) => Promise<string>;
  jetzt?: () => number;
  /** Plant `f` in `ms`; liefert eine Abbruchfunktion. */
  plane?: (ms: number, f: () => void) => () => void;
  online?: () => boolean;
}

export type InitErgebnis = 'aktiviert' | 'fortgesetzt' | 'neu';

export class GeraeteAktivierung {
  private anzeige: AktivierungsAnzeige = { art: 'nichtAktiviert' };
  private readonly hoerer = new Set<(a: AktivierungsAnzeige) => void>();
  private sitzung: GespeicherteSitzung | null = null;
  /** Jede neue/abgebrochene Sitzung erhöht den Zähler; ältere Antworten werden verworfen. */
  private generation = 0;
  private geplant: (() => void) | null = null;
  private fehlversuche = 0;
  private laeuftAbfrage = false;
  private zugangNachErfolg: GeraeteZugang | null = null;

  private readonly zufall: (n: number) => Uint8Array;
  private readonly hash: (text: string) => Promise<string>;
  private readonly jetzt: () => number;
  private readonly plane: (ms: number, f: () => void) => () => void;
  private readonly online: () => boolean;

  constructor(private readonly opt: AktivierungsOptionen) {
    this.zufall = opt.zufall ?? ((n) => crypto.getRandomValues(new Uint8Array(n)));
    this.hash = opt.hash ?? sha256Hex;
    this.jetzt = opt.jetzt ?? (() => Date.now());
    this.plane =
      opt.plane ??
      ((ms, f) => {
        const t = setTimeout(f, ms);
        return () => {
          clearTimeout(t);
        };
      });
    this.online = opt.online ?? (() => typeof navigator === 'undefined' || navigator.onLine);
  }

  aktuell(): AktivierungsAnzeige {
    return this.anzeige;
  }

  /** Zugang nach „erfolgreich“ (für den `ZugangsHalter` der App), sonst null. */
  zugang(): GeraeteZugang | null {
    return this.zugangNachErfolg;
  }

  abonniere(f: (a: AktivierungsAnzeige) => void): () => void {
    this.hoerer.add(f);
    return () => {
      this.hoerer.delete(f);
    };
  }

  /**
   * Beim App-Start: vorhandener Zugang → „erfolgreich“ (aktiviert); laufende, noch gültige
   * Sitzung → fortsetzen; abgelaufene oder kaputte Sitzung → verwerfen. Ein unlesbarer Zugang
   * wirft `TresorUnlesbar` (die App wählt dann über `ermittleEinrichtung`).
   */
  async initialisiere(): Promise<InitErgebnis> {
    const gen = ++this.generation;
    const z = await ladeZugang(this.opt.speicher);
    if (gen !== this.generation) {
      return 'neu';
    }
    if (z !== null) {
      // Absturz nach dem Speichern des Zugangs, vor dem Löschen der Sitzung: aufräumen
      await this.opt.speicher.loesche(SITZUNG_EINTRAG).catch(() => undefined);
      this.erfolg(z);
      return 'aktiviert';
    }
    let gespeichert: GespeicherteSitzung | null = null;
    try {
      const roh = await this.opt.speicher.lese(SITZUNG_EINTRAG);
      const wert: unknown = roh === null ? null : JSON.parse(roh);
      gespeichert = istSitzung(wert) ? wert : null;
      if (roh !== null && gespeichert === null) {
        await this.opt.speicher.loesche(SITZUNG_EINTRAG);
      }
    } catch {
      await this.opt.speicher.loesche(SITZUNG_EINTRAG).catch(() => undefined);
    }
    if (gen !== this.generation) {
      return 'neu';
    }
    // C11: mit erzeugtem Schlüssel entscheidet allein der Server (er kann ihn schon gesetzt haben)
    if (
      gespeichert === null ||
      (gespeichert.schluesselGeheim === undefined && gespeichert.gueltigBis <= this.jetzt())
    ) {
      if (gespeichert !== null) {
        await this.opt.speicher.loesche(SITZUNG_EINTRAG).catch(() => undefined);
      }
      this.setze({ art: 'nichtAktiviert' });
      return 'neu';
    }
    this.sitzung = gespeichert;
    this.setze(
      gespeichert.schluesselGeheim === undefined
        ? this.warteAnzeige(gespeichert, 'ok')
        : {
            art: 'laeuft',
            unternehmerName: gespeichert.unternehmerName ?? null,
            kassenName: gespeichert.kassenName ?? null,
            verbindung: 'ok',
          },
    );
    // Sofort einmal nachfragen: vielleicht wurde während des Neustarts schon zugeordnet
    this.planeAbfrage(gen, 0);
    return 'fortgesetzt';
  }

  /** „Mit QR-Code/Aktivierungscode verbinden“ bzw. „Neuen Code anfordern“. */
  async starte(): Promise<void> {
    // C11: Sitzung mit erzeugtem Schlüssel nie verwerfen, sondern weiter abholen – der Server
    // hat den Hash vielleicht schon gesetzt und den bisherigen Zugang damit ersetzt
    const offen = this.sitzung ?? (await this.leseSitzung());
    if (offen?.schluesselGeheim !== undefined) {
      this.setzeFort(offen);
      return;
    }
    // Eine laufende Sitzung lokal verwerfen; am Server läuft sie ab (keine Geräteroute zum
    // Abbrechen im Vertrag) – ohne ihr Geheimnis kann sie niemand mehr abholen.
    await this.verwirfLaufende();
    const gen = ++this.generation;
    if (!this.online()) {
      this.fehler('offline');
      return;
    }
    this.setze({ art: 'startet' });
    let start: StartAntwort;
    try {
      const schluessel = (await this.opt.oeffentlicherSchluessel?.().catch(() => null)) ?? null;
      start = await this.opt.schnittstelle.starte({
        geraet: this.opt.geraet(),
        ...(schluessel === null ? {} : { oeffentlicherSchluessel: schluessel }),
      });
    } catch (e) {
      if (gen === this.generation) {
        this.fehler(startFehler(e));
      }
      return;
    }
    if (gen !== this.generation) {
      return;
    }
    const s: GespeicherteSitzung = {
      v: 2,
      geraeteGeheimnis: start.geraeteGeheimnis,
      benutzerCode: start.benutzerCode,
      qrInhalt: pruefeQrInhalt(start.qrInhalt),
      gueltigBis: lokaleFrist(start.ablaufAm, this.jetzt()),
      intervall: begrenze(start.intervallSekunden),
    };
    try {
      await this.opt.speicher.schreibe(SITZUNG_EINTRAG, JSON.stringify(s));
    } catch {
      if (gen === this.generation) {
        this.fehler('speicher');
      }
      return;
    }
    if (gen !== this.generation) {
      return;
    }
    this.sitzung = s;
    this.fehlversuche = 0;
    this.setze(this.warteAnzeige(s, 'ok'));
    this.planeAbfrage(gen, s.intervall * 1000);
  }

  /** „Abbrechen“ am Gerät: Sitzung lokal verwerfen (läuft am Server ab). */
  async abbrechen(): Promise<void> {
    if (this.sitzung?.schluesselGeheim !== undefined) {
      // C11: nur anhalten; die Sitzung bleibt gespeichert, „starte“/Neustart holt weiter ab
      this.geplant?.();
      this.geplant = null;
      this.sitzung = null;
      this.generation++;
      this.setze({ art: 'nichtAktiviert' });
      return;
    }
    await this.verwirfLaufende();
    this.generation++;
    this.setze({ art: 'nichtAktiviert' });
  }

  /** Wieder online: nach einem Fehlversuch nicht den vollen Rückzug abwarten. */
  beiWiederverbindung(): void {
    if (this.sitzung !== null && this.fehlversuche > 0 && !this.laeuftAbfrage) {
      this.planeAbfrage(this.generation, this.sitzung.intervall * 1000);
    }
  }

  /** Zeitgeber stoppen (Seite verlassen); die Sitzung bleibt für `initialisiere` gespeichert. */
  beende(): void {
    this.generation++;
    this.geplant?.();
    this.geplant = null;
  }

  // ---- intern ----

  private warteAnzeige(s: GespeicherteSitzung, verbindung: Verbindung): AktivierungsAnzeige {
    return {
      art: 'wartet',
      benutzerCode: s.benutzerCode,
      qrInhalt: s.qrInhalt,
      gueltigBis: s.gueltigBis,
      verbindung,
    };
  }

  private mitVerbindung(verbindung: Verbindung): void {
    const a = this.anzeige;
    if (
      (a.art === 'wartet' || a.art === 'zuordnung' || a.art === 'laeuft') &&
      a.verbindung !== verbindung
    ) {
      this.setze({ ...a, verbindung });
    }
  }

  private planeAbfrage(gen: number, ms: number): void {
    this.geplant?.();
    this.geplant = this.plane(ms, () => {
      this.geplant = null;
      void this.frageAb(gen);
    });
  }

  private rueckzugMs(s: GespeicherteSitzung): number {
    const basis = s.intervall * 1000;
    return Math.min(basis * 2 ** Math.min(this.fehlversuche, 5), Math.max(MAX_RUECKZUG_MS, basis));
  }

  private async frageAb(gen: number): Promise<void> {
    const s = this.sitzung;
    if (gen !== this.generation || s === null || this.laeuftAbfrage) {
      return;
    }
    if (s.schluesselGeheim === undefined && this.jetzt() >= s.gueltigBis) {
      await this.beendeSitzung(gen, 'abgelaufen');
      return;
    }
    this.laeuftAbfrage = true;
    let ergebnis: AbholenErgebnis;
    try {
      const hash =
        s.kassenId !== undefined && s.schluesselGeheim !== undefined
          ? await this.hash(`${s.kassenId}.${s.schluesselGeheim}`)
          : undefined;
      ergebnis = await this.opt.schnittstelle.abholen(s.geraeteGeheimnis, hash);
    } catch (e) {
      this.laeuftAbfrage = false;
      if (gen !== this.generation) {
        return;
      }
      if (e instanceof AktivierungsFehler && e.art === 'abgelehnt') {
        await this.beendeSitzung(gen, endgrund(e));
        return;
      }
      this.fehlversuche++;
      this.mitVerbindung('unterbrochen');
      this.planeAbfrage(gen, this.rueckzugMs(s));
      return;
    }
    this.laeuftAbfrage = false;
    if (gen !== this.generation) {
      return;
    }
    this.fehlversuche = 0;
    await this.verarbeite(gen, s, ergebnis);
  }

  private async verarbeite(gen: number, s: GespeicherteSitzung, e: AbholenErgebnis): Promise<void> {
    switch (e.zustand) {
      case 'langsamer': {
        s.intervall = begrenze(e.intervallSekunden ?? s.intervall + INTERVALL_AUFSCHLAG_S);
        await this.merkeSitzung(s);
        this.mitVerbindung('ok');
        this.planeAbfrage(gen, Math.max(s.intervall, e.warteSekunden ?? 0) * 1000);
        return;
      }
      case 'wartend':
        this.uebernimm(s, e.intervallSekunden, e.ablaufAm);
        this.setze(this.warteAnzeige(s, 'ok'));
        this.planeAbfrage(gen, s.intervall * 1000);
        return;
      case 'zugeordnet':
        this.uebernimm(s, e.intervallSekunden, e.ablaufAm);
        if (s.unternehmerName !== e.unternehmerName || s.kassenName !== e.kassenName) {
          s.unternehmerName = e.unternehmerName;
          s.kassenName = e.kassenName;
          await this.merkeSitzung(s);
        }
        this.setze({
          art: 'zuordnung',
          bestaetigungsCode: e.bestaetigungsCode,
          unternehmerName: e.unternehmerName,
          kassenName: e.kassenName,
          gueltigBis: s.gueltigBis,
          verbindung: 'ok',
        });
        this.planeAbfrage(gen, s.intervall * 1000);
        return;
      case 'bestaetigt':
        this.uebernimm(s, e.intervallSekunden, e.ablaufAm);
        await this.bereiteSchluesselVor(gen, s, e.kassenId, e.unternehmerName);
        return;
      case 'abgeholt':
        await this.uebernimmZugang(gen, s, e);
        return;
      case 'abgelaufen':
      case 'abgebrochen':
      case 'gesperrt':
        await this.beendeSitzung(gen, e.zustand);
        return;
    }
  }

  private uebernimm(s: GespeicherteSitzung, intervall: number, ablaufAm: string): void {
    // Der Server darf verlangsamen, aber nie unter ein schon verlangtes Intervall drücken
    s.intervall = Math.max(s.intervall, begrenze(intervall));
    s.gueltigBis = lokaleFrist(ablaufAm, this.jetzt());
  }

  /**
   * `bestaetigt`: Kassenschlüssel `<kassenId>.<geheim>` erzeugen und ZUERST im sicheren Speicher
   * merken; erst die nächste Abfrage sendet seinen Hash (Intervall bleibt gewahrt).
   */
  private async bereiteSchluesselVor(
    gen: number,
    s: GespeicherteSitzung,
    kassenId: string,
    unternehmerName: string,
  ): Promise<void> {
    if (s.kassenId !== kassenId || s.schluesselGeheim === undefined) {
      s.kassenId = kassenId;
      s.schluesselGeheim = base64url(this.zufall(32));
    }
    s.unternehmerName = unternehmerName;
    try {
      await this.opt.speicher.schreibe(SITZUNG_EINTRAG, JSON.stringify(s));
    } catch {
      // Ohne gemerkten Schlüssel nie den Hash senden – sonst könnte ein Absturz den gesetzten
      // Schlüssel unwiederbringlich verlieren
      if (gen === this.generation) {
        await this.beendeSitzung(gen, 'speicher');
      }
      return;
    }
    if (gen !== this.generation) {
      return;
    }
    this.setze({
      art: 'laeuft',
      unternehmerName,
      kassenName: s.kassenName ?? null,
      verbindung: 'ok',
    });
    this.planeAbfrage(gen, s.intervall * 1000);
  }

  private async uebernimmZugang(
    gen: number,
    s: GespeicherteSitzung,
    e: Extract<AbholenErgebnis, { zustand: 'abgeholt' }>,
  ): Promise<void> {
    if (
      !e.kassenschluesselGesetzt ||
      s.schluesselGeheim === undefined ||
      s.kassenId !== e.kassenId
    ) {
      // Ein anderer Schlüssel wurde gesetzt (oder keiner von uns): dieser gilt nicht
      await this.beendeSitzung(gen, 'fehler');
      return;
    }
    const zugang: GeraeteZugang = {
      kassenId: e.kassenId,
      geraetId: e.geraetId,
      nachweis: `${e.kassenId}.${s.schluesselGeheim}`,
      kasseName: s.kassenName ?? '',
      unternehmerName: e.unternehmerName,
      aktiviertAm: this.jetzt(),
    };
    try {
      await speichereZugang(this.opt.speicher, zugang);
    } catch {
      // Sitzung (mit dem Schlüssel) bleibt erhalten: ein Neustart kann es erneut versuchen
      if (gen === this.generation) {
        this.geplant?.();
        this.geplant = null;
        this.fehler('speicher');
      }
      return;
    }
    if (this.opt.spiegel !== undefined) {
      try {
        // Reihenfolge wie bisher (aktivierung.ts): zuerst Kassen-ID, dann der Schlüssel
        this.opt.spiegel.setItem(ALT_KASSEN_ID, zugang.kassenId);
        this.opt.spiegel.setItem(ALT_KASSENSCHLUESSEL, zugang.nachweis);
      } catch {
        // Spiegel ist nur Übergang; der sichere Speicher ist maßgeblich
      }
    }
    await this.opt.speicher.loesche(SITZUNG_EINTRAG).catch(() => undefined);
    if (gen !== this.generation) {
      return;
    }
    this.sitzung = null;
    this.geplant?.();
    this.geplant = null;
    this.erfolg(zugang);
  }

  private async leseSitzung(): Promise<GespeicherteSitzung | null> {
    try {
      const roh = await this.opt.speicher.lese(SITZUNG_EINTRAG);
      const wert: unknown = roh === null ? null : JSON.parse(roh);
      return istSitzung(wert) ? wert : null;
    } catch {
      return null;
    }
  }

  private setzeFort(s: GespeicherteSitzung): void {
    const gen = ++this.generation;
    this.sitzung = s;
    this.fehlversuche = 0;
    this.laeuftAbfrage = false;
    this.setze({
      art: 'laeuft',
      unternehmerName: s.unternehmerName ?? null,
      kassenName: s.kassenName ?? null,
      verbindung: 'ok',
    });
    this.planeAbfrage(gen, 0);
  }

  private async merkeSitzung(s: GespeicherteSitzung): Promise<void> {
    await this.opt.speicher.schreibe(SITZUNG_EINTRAG, JSON.stringify(s)).catch(() => undefined);
  }

  private async beendeSitzung(gen: number, grund: FehlerGrund): Promise<void> {
    this.geplant?.();
    this.geplant = null;
    this.sitzung = null;
    await this.opt.speicher.loesche(SITZUNG_EINTRAG).catch(() => undefined);
    if (gen === this.generation) {
      this.fehler(grund);
    }
  }

  private async verwirfLaufende(): Promise<void> {
    this.geplant?.();
    this.geplant = null;
    const s = this.sitzung;
    this.sitzung = null;
    this.fehlversuche = 0;
    this.laeuftAbfrage = false;
    if (s !== null) {
      await this.opt.speicher.loesche(SITZUNG_EINTRAG).catch(() => undefined);
    }
  }

  private erfolg(z: GeraeteZugang): void {
    this.zugangNachErfolg = z;
    // Der Nachweis bleibt aus der Anzeige heraus
    this.setze({
      art: 'erfolgreich',
      zugang: {
        kassenId: z.kassenId,
        geraetId: z.geraetId,
        kasseName: z.kasseName,
        unternehmerName: z.unternehmerName,
        aktiviertAm: z.aktiviertAm,
      },
    });
  }

  private fehler(grund: FehlerGrund): void {
    this.setze({ art: 'fehlgeschlagen', grund, meldung: FEHLERTEXTE[grund] });
  }

  private setze(a: AktivierungsAnzeige): void {
    this.anzeige = a;
    for (const f of this.hoerer) {
      f(a);
    }
  }
}

function begrenze(intervall: number): number {
  return Math.min(Math.max(intervall, INTERVALL_START_S), INTERVALL_MAX_S);
}

function startFehler(e: unknown): FehlerGrund {
  if (e instanceof AktivierungsFehler) {
    if (e.art === 'begrenzt') {
      return 'zuVieleVersuche';
    }
    if (e.art === 'abgelehnt') {
      return 'fehler';
    }
  }
  return 'offline';
}

function endgrund(e: AktivierungsFehler): FehlerGrund {
  if (e.zustand === 'abgelaufen' || e.zustand === 'abgebrochen' || e.zustand === 'gesperrt') {
    return e.zustand;
  }
  // 404 `unbekannt`: Sitzung unbekannt oder abgelaufen (bewusst nicht unterscheidbar)
  if (e.status === 404) {
    return 'abgelaufen';
  }
  return 'fehler';
}
